// ONB-PARENT: the wizard shell. A body-level layer (never openModal, never re()): fixed-size card, direction-aware slide, Back/Skip/Next, per-step contract.
//
// STEP CONTRACT (a step is registered with this shape; ONB-2..5 add the real ones):
//   { id, order, applicable?(ctx)->bool, label()->string (summary row), summary?(result)->string,
//     mount(api) -> { el: HTMLElement,                         // built once at open, WITHOUT side effects (the shell measures every step to size the window)
//                     isDone()->bool,                           // drives Next.disabled
//                     commit()->Promise<{ok:boolean,error?:string}|void>,   // runs on Next only, may fail (the step stays, the error is shown); Skip never calls it
//                     onEnter?(info), onLeave?(), relang?(), destroy?() } }
// api: { T, ctx, isRtl(), refresh() (re-evaluate Next), setError(msg), state() }
// RULES: Next commits and is disabled until isDone(); Skip declines and calls NOTHING of the step (no commit, no data write); Back never commits;
//   a step already committed keeps its 'done' result even if the user goes Back and presses Skip.
import { injectOnbStyles } from './styles.js';
import * as ST from './state.js';

const SUMMARY_ID = '__summary';
const MARK = (n) => String.fromCharCode(0xE000 + n);

export function createWizard(o) {
  const T = o.T, isRtl = o.isRtl, host = o.host || document.body, now = o.now || (() => new Date().toISOString());
  const reduced = () => (o.reducedMotion ? o.reducedMotion() : !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches));
  injectOnbStyles();
  let rtl = !!isRtl();
  const defs = o.steps.slice();                                  // the visible, ordered step definitions (the summary is appended below)
  const all = defs.concat([{ id: SUMMARY_ID, order: 1e9 }]);
  let state = o.state || null;                                   // null until the first Next/Skip (nothing is written before the user acts)
  const results = Object.assign({}, state && state.steps);       // id -> 'done' | 'skipped'
  const stack = [];                                              // visited step indices (Back returns to a step the user actually saw)
  let idx = ST.startIndex(all, state), busy = false, closed = false, errEl = null;
  const ctls = [], sEls = [];
  const api = (id) => ({ T, ctx: o.ctx, isRtl: () => rtl, state: () => state, refresh: () => syncNext(), setError: (m) => setErr(id, m) });

  // ── DOM ──
  const overlay = document.createElement('div'); overlay.className = 'onb-overlay'; overlay.dir = rtl ? 'rtl' : 'ltr';
  overlay.setAttribute('data-onb', '1');
  const card = document.createElement('div'); card.className = 'onb-card'; card.setAttribute('role', 'dialog'); card.setAttribute('aria-modal', 'true'); card.tabIndex = -1;
  const titleId = 'onb-title-' + Math.random().toString(36).slice(2, 7);
  card.setAttribute('aria-labelledby', titleId);
  const top = document.createElement('div'); top.className = 'onb-top';
  const brand = document.createElement('div'); brand.className = 'onb-brand'; const dot = document.createElement('span'); dot.className = 'onb-dot'; dot.textContent = 'H'; dot.setAttribute('aria-hidden', 'true');
  const brandTx = document.createElement('span'); brandTx.id = titleId; brand.append(dot, brandTx);
  const count = document.createElement('div'); count.className = 'onb-count';
  top.append(brand, count);
  const prog = document.createElement('div'); prog.className = 'onb-progress'; prog.setAttribute('aria-hidden', 'true');
  const body = document.createElement('div'); body.className = 'onb-body';
  const foot = document.createElement('div'); foot.className = 'onb-foot';
  const back = document.createElement('button'); back.type = 'button'; back.className = 'onb-btn-back';
  back.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3L5 8l5 5"/></svg>';
  const backTx = document.createElement('span'); back.appendChild(backTx);
  const skip = document.createElement('button'); skip.type = 'button'; skip.className = 'onb-btn-skip';
  const next = document.createElement('button'); next.type = 'button'; next.className = 'onb-btn-next';
  const fe = document.createElement('div'); fe.className = 'onb-foot-end'; fe.append(skip, next);
  foot.append(back, fe);
  const live = document.createElement('div'); live.className = 'onb-live'; live.setAttribute('aria-live', 'polite');
  card.append(top, prog, body, foot, live); overlay.appendChild(card);

  // each step: a wrapper in the body; the real steps are built once (no side effects), the summary is rebuilt on entry
  all.forEach((d, i) => {
    const w = document.createElement('section'); w.className = 'onb-step'; w.setAttribute('data-step', d.id); w.setAttribute('aria-hidden', 'true'); sEls.push(w); body.appendChild(w);
    if (d.id === SUMMARY_ID) { ctls.push(null); return; }
    let c = null;
    try { c = d.mount(api(d.id)); } catch (e) { console.error('❌ onboarding step ' + d.id + ' mount:', e); }
    ctls.push(c); if (c && c.el) w.appendChild(c.el);
  });

  function setErr(id, m) { const i = all.findIndex(s => s.id === id); const e = sEls[i] && sEls[i].querySelector('.onb-error'); if (e) e.textContent = m || ''; }

  // ── chrome text ──
  function setCount() {
    const x = idx + 1, y = all.length;
    const tpl = String(T('onb_step_x_of_y', { x: MARK(1), y: MARK(2) }));
    count.textContent = '';
    for (const part of tpl.split(new RegExp('(' + MARK(1) + '|' + MARK(2) + ')'))) {
      if (part === MARK(1) || part === MARK(2)) { const b = document.createElement('bdi'); b.textContent = String(part === MARK(1) ? x : y); count.appendChild(b); }
      else if (part) count.appendChild(document.createTextNode(part));
    }
  }
  function setProgress() {
    while (prog.children.length < all.length) prog.appendChild(document.createElement('span'));
    while (prog.children.length > all.length) prog.lastChild.remove();
    [...prog.children].forEach((b, j) => { b.className = j < idx ? 'is-done' : j === idx ? 'is-cur' : ''; });
  }
  const isLast = () => idx === all.length - 1;
  function chrome() {
    brandTx.textContent = T('onb_brand'); backTx.textContent = T('onb_back'); skip.textContent = T('onb_skip');
    next.textContent = isLast() ? T('onb_open_app') : T('onb_next');
    setCount(); setProgress();
    back.hidden = idx === 0 || isLast() || stack.length === 0;
    skip.hidden = isLast();
    back.setAttribute('aria-hidden', back.hidden ? 'true' : 'false'); skip.setAttribute('aria-hidden', skip.hidden ? 'true' : 'false');
    back.tabIndex = back.hidden ? -1 : 0; skip.tabIndex = skip.hidden ? -1 : 0;
    syncNext();
  }
  function done() { if (isLast()) return true; const c = ctls[idx]; try { return !!(c && c.isDone && c.isDone()); } catch (_) { return false; } }
  function syncNext() {
    if (!next) return;
    next.disabled = busy || !done();
    if (next.disabled && !busy && !isLast()) next.title = T('onb_next_hint'); else next.removeAttribute('title');
    card.classList.toggle('is-busy', busy);
  }

  // ── summary (built from the result map, never from clicks) ──
  function buildSummary() {
    const w = sEls[all.length - 1]; w.textContent = '';
    const wrap = document.createElement('div'); wrap.className = 'onb-done-wrap';
    const ic = document.createElement('div'); ic.className = 'onb-done-icon'; ic.textContent = '🎉'; ic.setAttribute('aria-hidden', 'true');
    const h = document.createElement('h2'); h.textContent = T('onb_done_title');
    const p = document.createElement('p'); p.className = 'onb-sub'; p.textContent = T('onb_done_sub');
    const list = document.createElement('div'); list.className = 'onb-done-list';
    for (const d of defs) {
      const r = results[d.id]; const row = document.createElement('div');
      const label = d.label ? d.label() : d.id;
      const line = d.summary ? d.summary(r || 'skipped') : (r === 'done' ? T('onb_sum_done', { label }) : T('onb_sum_skipped', { label }));
      row.textContent = (r === 'done' ? '✅ ' : '⏭️ ') + line; row.setAttribute('data-sum', d.id); row.setAttribute('data-result', r || 'skipped');
      list.appendChild(row);
    }
    wrap.append(ic, h, p, list); w.appendChild(wrap);
  }

  // ── sizing: the window height is the tallest step in the CURRENT language, measured, never guessed ──
  function measure() {
    card.classList.add('is-measuring');
    let max = 0; sEls.forEach((w, i) => { if (i === all.length - 1) return; max = Math.max(max, w.getBoundingClientRect().height); });
    // the summary can be as tall as the number of steps: measure it too
    const si = all.length - 1; const wasHtml = sEls[si].innerHTML; buildSummary(); max = Math.max(max, sEls[si].getBoundingClientRect().height);
    card.classList.remove('is-measuring');
    const chromeH = top.offsetHeight + prog.offsetHeight + foot.offsetHeight;
    const cap = Math.max(240, (window.innerHeight || 700) - 32 - chromeH);
    const h = Math.max(300, Math.min(Math.ceil(max), cap));
    body.style.height = h + 'px';
    if (idx !== si) sEls[si].innerHTML = wasHtml;
    return h;
  }

  // ── navigation ──
  function show(i, first) {
    sEls.forEach((w, j) => { const on = j === i; w.classList.toggle('is-active', on); w.setAttribute('aria-hidden', on ? 'false' : 'true'); });
    idx = i; chrome();
    const c = ctls[i]; try { if (c && c.onEnter) c.onEnter({ first: !!first, result: results[all[i].id] || null }); } catch (e) { console.error('❌ onboarding onEnter:', e); }
    const h = sEls[i].querySelector('h2'); live.textContent = h ? h.textContent : '';
  }
  function finishAnims() { try { (sEls.flatMap(w => w.getAnimations ? w.getAnimations() : [])).forEach(a => a.finish()); } catch (_) {} }
  function go(n, forward) {
    if (n === idx || n < 0 || n >= all.length) return;
    finishAnims();
    const from = idx, outEl = sEls[from]; try { const c = ctls[from]; if (c && c.onLeave) c.onLeave(); } catch (_) {}
    if (n === all.length - 1) buildSummary();
    idx = n;
    const inEl = sEls[n];
    const sign = ST.slideSign(forward, rtl), R = reduced(), D = R ? 120 : 180, dx = R ? 0 : 24;
    // the new step becomes the active one at once (focus, layout, pointer events); the outgoing one stays visible while it animates away
    outEl.style.visibility = 'visible'; outEl.classList.remove('is-active'); outEl.setAttribute('aria-hidden', 'true');
    inEl.classList.add('is-active'); inEl.setAttribute('aria-hidden', 'false');
    chrome();
    const c = ctls[n]; try { if (c && c.onEnter) c.onEnter({ first: false, result: results[all[n].id] || null }); } catch (e) { console.error('❌ onboarding onEnter:', e); }
    const h = inEl.querySelector('h2'); live.textContent = h ? h.textContent : '';
    if (outEl.animate) {
      const a1 = outEl.animate([{ opacity: 1, transform: 'translateX(0px)' }, { opacity: 0, transform: 'translateX(' + (-sign * dx) + 'px)' }], { duration: D, easing: 'ease-out', fill: 'both' });
      const a2 = inEl.animate([{ opacity: 0, transform: 'translateX(' + (sign * dx) + 'px)' }, { opacity: 1, transform: 'translateX(0px)' }], { duration: D, easing: 'ease-out', fill: 'both' });
      const fin = () => { try { a1.cancel(); a2.cancel(); } catch (_) {} outEl.style.visibility = ''; };
      a1.finished.then(fin, fin);
    } else outEl.style.visibility = '';
    focusFirst(inEl);
  }
  function focusFirst(scope) {
    const t = scope.querySelector('input,button,select,textarea,[tabindex]:not([tabindex="-1"])') || (next.disabled ? card : next);
    setTimeout(() => { try { (t && !t.disabled ? t : card).focus({ preventScroll: true }); } catch (_) {} }, 0);
  }
  const nextId = (i) => (all[i + 1] ? all[i + 1].id : null);
  function persist(s) { state = s; try { o.persist && o.persist(s); } catch (e) { console.error('❌ onboarding persist:', e); } }

  async function onNext() {
    if (busy || closed || next.disabled) return;
    if (isLast()) { persist(ST.finish(state || ST.fresh(all[0].id, now()), now())); close('done'); return; }
    const d = all[idx], c = ctls[idx]; busy = true; syncNext(); setErr(d.id, '');
    let res;
    try { res = c && c.commit ? await c.commit() : { ok: true }; } catch (e) { console.error('❌ onboarding commit ' + d.id + ':', e); res = { ok: false, error: (e && e.message) || '' }; }
    busy = false;
    if (closed) return;
    if (res && res.ok === false) { setErr(d.id, res.error || T('onb_error_generic')); syncNext(); return; }
    results[d.id] = 'done';
    persist(ST.record(state || ST.fresh(d.id, now()), d.id, 'done', nextId(idx), now()));
    stack.push(idx); go(idx + 1, true);
  }
  function onSkip() {
    if (busy || closed || isLast()) return;
    const d = all[idx];
    if (results[d.id] !== 'done') results[d.id] = 'skipped';       // Skip never touches the step: no commit, no data write; an earlier commit stays 'done'
    persist(ST.record(state || ST.fresh(d.id, now()), d.id, 'skipped', nextId(idx), now()));
    stack.push(idx); go(idx + 1, true);
  }
  function onBack() {
    if (busy || closed || !stack.length) return;
    const p = stack.pop(); go(p, false);
  }
  next.addEventListener('click', onNext); skip.addEventListener('click', onSkip); back.addEventListener('click', onBack);
  overlay.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {                                      // focus trap
      const f = [...card.querySelectorAll('input,button,select,textarea,a[href],[tabindex]')].filter(x => !x.disabled && x.tabIndex >= 0 && x.offsetParent !== null && !x.closest('[aria-hidden="true"]'));
      if (!f.length) { e.preventDefault(); card.focus(); return; }
      const a = f[0], z = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === a || document.activeElement === card)) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    } else if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT' && !next.disabled) { e.preventDefault(); onNext(); }
    else if (e.key === 'Escape') { e.stopPropagation(); }       // Esc does not close the wizard (a decision for planning: a stray key must not skip the rest for good)
  });

  // ── lifecycle ──
  const root = document.getElementById('root');
  function open() {
    host.appendChild(overlay);
    try { if (root) root.setAttribute('inert', ''); } catch (_) {}
    chrome(); show(idx, true);
    if (idx === all.length - 1) buildSummary();
    measure();
    focusFirst(sEls[idx]);
  }
  function close(why) {
    if (closed) return; closed = true;
    finishAnims();
    ctls.forEach((c) => { try { c && c.destroy && c.destroy(); } catch (_) {} });
    try { if (root) root.removeAttribute('inert'); } catch (_) {}
    const out = () => { overlay.remove(); try { o.onClose && o.onClose(why); } catch (_) {} };
    if (overlay.animate && !reduced()) { const a = overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' }); a.finished.then(out, out); } else out();
  }
  function relang() {
    rtl = !!isRtl(); overlay.dir = rtl ? 'rtl' : 'ltr';
    ctls.forEach((c) => { try { c && c.relang && c.relang(); } catch (_) {} });
    chrome(); if (isLast()) buildSummary(); measure();
  }
  function onResize() { if (!closed) measure(); }
  window.addEventListener('resize', onResize);
  const origClose = close;
  return {
    open, close: (w) => { window.removeEventListener('resize', onResize); origClose(w); }, relang, el: overlay,
    _debug: { get idx() { return idx; }, get state() { return state; }, get results() { return Object.assign({}, results); }, get stack() { return stack.slice(); }, steps: () => all.map(s => s.id), measure, go, ctl: (i) => ctls[i] },
  };
}
